import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Clock, Users, MapPin, ArrowLeft, Check, Heart, Ticket } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";
import { PhotoGallery } from "@/components/PhotoGallery";
import { ShareButton, useSeo } from "@/components/Share";

export default function ExperienceDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [exp, setExp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState(1);
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/experiences/${slug}`).then((r) => setExp(r.data)).catch(() => nav("/experiences"));
  }, [slug, nav]);
  useSeo({ item: exp, kind: "experiences", slug, title: exp?.title || "", description: exp?.short_description || exp?.description, image: exp?.image });

  const book = async () => {
    if (!exp.price_lookup_key) return toast("Contact host to book.");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: exp.price_lookup_key,
        quantity: qty,
        origin_url: window.location.origin,
        item_type: "experience",
        item_id: exp.id,
      });
      window.location.href = data.checkout_url;
    } catch { toast.error("Couldn't start checkout"); }
    finally { setBusy(false); }
  };

  const fav = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/favorites", { item_type: "experience", item_id: exp.id });
      toast.success("Saved");
    } catch { toast.error("Couldn't save"); }
  };

  if (!exp) return <div className="p-20 text-center text-[color:var(--forest-2)]">Loading…</div>;

  return (
    <div>
      <div className="relative h-[65vh] min-h-[480px] overflow-hidden">
        <img src={exp.image} alt={exp.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        <button onClick={() => nav(-1)} className="absolute top-6 left-6 btn-pill btn-outline !border-white/40 !text-white bg-black/20 backdrop-blur">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="absolute bottom-10 left-0 right-0 px-6 lg:px-10">
          <div className="max-w-[1200px] mx-auto text-white">
            <div className="chip !bg-white/15 !text-white !border-white/30 backdrop-blur mb-3">{exp.category.replace("-", " ")}</div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl">
              {exp.title}
            </motion.h1>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5"><Star size={14} className="fill-white" /> {exp.rating} ({exp.review_count})</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {exp.duration_hours}h</span>
              <span className="inline-flex items-center gap-1.5"><Users size={14} /> up to {exp.max_guests}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {exp.location}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-4 pb-8 border-b border-[color:var(--forest-line)]">
            <img src={exp.host.avatar} alt={exp.host.name} className="w-16 h-16 rounded-full object-cover" />
            <div>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)]">Hosted by</div>
              <div className="font-display text-2xl">{exp.host.name}</div>
              <div className="text-sm text-[color:var(--forest-2)]">{exp.host.specialties.join(" · ")}</div>
            </div>
          </div>

          <div className="prose-heygc mt-10">
            <p>{exp.description}</p>
            <p>{exp.host.bio}</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display text-xl mb-4">Highlights</h3>
              <ul className="space-y-2 text-sm">
                {exp.highlights.map((h) => <li key={h} className="flex gap-2"><Check size={15} className="mt-0.5 text-[color:var(--tropical-sea)]" /> {h}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-xl mb-4">Inclusions</h3>
              <ul className="space-y-2 text-sm">
                {exp.inclusions.map((h) => <li key={h} className="flex gap-2"><Check size={15} className="mt-0.5 text-[color:var(--tropical-sea)]" /> {h}</li>)}
              </ul>
            </div>
          </div>
          <PhotoGallery images={exp.gallery} title={exp.title} />
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-28 bg-white rounded-3xl border border-[color:var(--forest-line)] p-8 shadow-[0_20px_60px_-30px_rgba(11,37,36,0.2)]">
            <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[color:var(--forest-2)] mb-2">Per guest</div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl">${exp.price}</span>
              <span className="text-[color:var(--forest-2)]">AUD</span>
            </div>
            <div className="mt-6 flex items-center justify-between border border-[color:var(--forest-line)] rounded-full px-4 py-2">
              <span className="text-sm">Guests</span>
              <div className="flex items-center gap-3">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 rounded-full border">−</button>
                <span className="min-w-4 text-center font-medium">{qty}</span>
                <button onClick={() => setQty(Math.min(exp.max_guests, qty + 1))} className="w-7 h-7 rounded-full border">+</button>
              </div>
            </div>
            <button
              onClick={book}
              disabled={busy}
              data-testid={TID.expBookBtn}
              className="mt-6 btn-pill btn-primary w-full text-base py-4 disabled:opacity-50"
            >
              <Ticket size={16} /> {busy ? "Redirecting…" : "Book experience"}
            </button>
            <button onClick={fav} data-testid={TID.favBtn(exp.id)} className="mt-3 btn-pill btn-outline w-full py-3">
              <Heart size={15} /> Save
            </button>
            <ShareButton kind="experiences" slug={exp.slug} title={exp.title} className="mt-3 w-full py-3 justify-center" />
            <div className="mt-6 text-xs text-[color:var(--forest-2)] leading-relaxed">
              Free cancellation up to 48 hours before start. Small-group experience, limited to {exp.max_guests} guests.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
